// src/components/ProfileModal.tsx
import React, { useRef, useState } from 'react';
import { API_URL } from '../config';
import { User } from '../hooks/useAuth';

interface Props {
  user: User;
  token: string;
  onAvatarUpdate: (url: string) => void;
  onClose: () => void;
  onLogout: () => void;
}

export default function ProfileModal({ user, token, onAvatarUpdate, onClose, onLogout }: Props) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  // Initialize preview from current user avatar
  const [preview, setPreview] = useState(user.avatar_url || '');

  // FIX: always read the freshest token available — prop may be stale if
  // the component mounted before useAuth finished its server verification
  const getAuthToken = (): string =>
    token || localStorage.getItem('coderai_token') || '';

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size on client side before uploading (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setError('File too large. Maximum size is 2MB.');
      return;
    }

    // Show local preview immediately for fast feedback
    const reader = new FileReader();
    reader.onload = ev => setPreview(ev.target?.result as string);
    reader.readAsDataURL(file);

    setUploading(true);
    setError('');
    setSuccess('');

    const form = new FormData();
    form.append('avatar', file);

    // FIX: read token fresh at call time — not from the stale prop closure
    const authToken = getAuthToken();
    if (!authToken) {
      setError('Session expired. Please log in again.');
      setUploading(false);
      return;
    }

    try {
      const r = await fetch(`${API_URL}/upload_avatar.php`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}` },
        body: form
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Upload failed');

      // Use the server-returned URL (canonical) as the preview
      setPreview(data.avatar_url);
      // Propagate up so App state and sidebar avatar update immediately
      onAvatarUpdate(data.avatar_url);
      setSuccess('Profile photo updated!');
    } catch (e: any) {
      setError(e.message);
      // Revert preview to original on failure
      setPreview(user.avatar_url || '');
    } finally {
      setUploading(false);
      // Reset file input so same file can be re-selected if needed
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>✕</button>
        <h3 className="modal-title">Profile</h3>

        <div className="avatar-wrapper" onClick={() => !uploading && fileRef.current?.click()}>
          {preview ? (
            <img src={preview} alt="avatar" className="avatar-img" />
          ) : (
            <div className="avatar-placeholder">
              {user.name.charAt(0).toUpperCase()}
            </div>
          )}
          <div className="avatar-overlay">
            {uploading ? '⏳ Uploading…' : '📷 Change'}
          </div>
        </div>

        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />

        {error && <p className="auth-error" style={{ marginTop: 8 }}>{error}</p>}
        {success && (
          <p style={{
            marginTop: 8, color: '#4ade80', fontSize: 13,
            background: '#0d2b1a', border: '1px solid #4ade80',
            borderRadius: 8, padding: '8px 12px', textAlign: 'center'
          }}>{success}</p>
        )}

        <div className="profile-info">
          <p className="profile-name">{user.name}</p>
          <p className="profile-email">{user.email}</p>
        </div>

        <button className="auth-btn logout-btn" onClick={onLogout}>Sign Out</button>
      </div>
    </div>
  );
}